import { isAppLocale } from '~/constants/appLocale'
import { marketingConsentText } from '~/constants/marketingConsent'

/**
 * The marketing opt-in on sign-up forms and in the profile.
 *
 * An account's address is verified by Supabase, so its opt-in needs no
 * confirmation e-mail: it goes straight to /api/newsletter/me. A sign-up that
 * still waits for the verification e-mail has no session yet, so the ticked box
 * travels in the user metadata (`marketing_consent`) and is applied by
 * /auth/callback once the address is confirmed.
 */
export const MARKETING_CONSENT_METADATA = 'marketing_consent'
/**
 * The same box ticked before "Continue with Google": OAuth carries no metadata
 * of ours, so the tick waits in this browser for /auth/callback instead.
 */
export const MARKETING_CONSENT_PENDING_KEY = 'pendingMarketingConsent'

export function useMarketingConsent() {
  const { locale } = useI18n()
  const client = useSupabaseClient()

  const consentText = computed(() => marketingConsentText(locale.value))
  const apiLocale = () => (isAppLocale(locale.value) ? locale.value : 'en')

  async function setMarketingConsent(subscribed: boolean, source: 'registration' | 'profile' = 'profile') {
    return await $fetch<{ status: 'none' | 'pending' | 'confirmed' | 'unsubscribed' }>('/api/newsletter/me', {
      method: 'PUT',
      body: { subscribed, locale: apiLocale(), source },
    })
  }

  /** Applies an opt-in ticked at sign-up, once the account can sign in. Never throws. */
  async function applyPendingRegistrationConsent() {
    try {
      let ticked = false
      try {
        ticked = localStorage.getItem(MARKETING_CONSENT_PENDING_KEY) === 'true'
        localStorage.removeItem(MARKETING_CONSENT_PENDING_KEY)
      } catch {
        // Blocked storage: only the e-mail sign-up's metadata can carry the tick.
      }
      const { data } = await client.auth.getUser()
      const inMetadata = data?.user?.user_metadata?.[MARKETING_CONSENT_METADATA] === true
      if (!ticked && !inMetadata) return
      await setMarketingConsent(true, 'registration')
      if (inMetadata) await client.auth.updateUser({ data: { [MARKETING_CONSENT_METADATA]: null } })
    } catch (err) {
      console.error('[marketing-consent] could not apply the sign-up opt-in:', err)
    }
  }

  return { consentText, setMarketingConsent, applyPendingRegistrationConsent }
}
